import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, message, Space, Modal, Alert } from 'antd';
import { EditOutlined, DeleteOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import SignatureCanvas from 'react-signature-canvas';
import api from '../../../axiosConfig';
import { useUser } from '../../../UserContext';
import './FirmaConductor.css';

/**
 * Componente para gestionar la firma del conductor
 * Permite al conductor guardar su firma para usarla en documentos
 */
const FirmaConductor = () => {
  const navigate = useNavigate();
  const { verificarFirma } = useUser();
  const [firmaActual, setFirmaActual] = useState(null);
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(true);
  const sigCanvas = useRef(null);

  useEffect(() => {
    cargarFirma();
  }, []);

  const cargarFirma = async () => {
    setCargando(true);
    try {
      const response = await api.get('/conductor/firma');
      if (response.data.success && response.data.data.tiene_firma) {
        setFirmaActual(response.data.data.firma_imagen);
      }
    } catch (error) {
      console.error('Error al cargar firma:', error);
    } finally {
      setCargando(false);
    }
  };

  const limpiarFirma = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const guardarFirma = async () => {
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      message.warning('Por favor, dibuja tu firma antes de guardar');
      return;
    }

    setGuardando(true);
    const esPrimeraVez = firmaActual === null;

    try {
      // Obtener la firma como base64
      const firmaBase64 = sigCanvas.current.toDataURL('image/png');

      const response = await api.post('/conductor/firma', {
        firma_imagen: firmaBase64
      });

      if (response.data.success) {
        setFirmaActual(firmaBase64);
        setEditando(false);

        // Actualizar estado de firma en UserContext
        await verificarFirma();

        if (esPrimeraVez) {
          message.success('Firma registrada exitosamente. Redirigiendo al inicio...');
          // Redirigir después de 1.5 segundos
          setTimeout(() => {
            navigate('/modules/PreOperationalForm');
          }, 1500);
        } else {
          message.success('Firma actualizada exitosamente');
        }
      } else {
        message.error('Error al guardar la firma');
      }
    } catch (error) {
      console.error('Error al guardar firma:', error);
      message.error('Error al guardar la firma');
    } finally {
      setGuardando(false);
    }
  };

  const eliminarFirma = () => {
    Modal.confirm({
      title: '¿Eliminar firma?',
      content: '¿Estás seguro que deseas eliminar tu firma? Tendrás que crearla nuevamente.',
      okText: 'Sí, eliminar',
      cancelText: 'Cancelar',
      okType: 'danger',
      onOk: async () => {
        try {
          console.log('=== ELIMINANDO FIRMA ===');
          const response = await api.post('/conductor/firma', {
            firma_imagen: null
          });

          console.log('Response eliminar firma:', response.data);

          if (response.data.success) {
            setFirmaActual(null);
            message.success('Firma eliminada exitosamente');

            // Actualizar estado de firma en UserContext
            await verificarFirma();
          } else {
            message.error('Error al eliminar la firma');
          }
        } catch (error) {
          console.error('Error al eliminar firma:', error);
          console.error('Error response:', error.response?.data);
          message.error(
            error.response?.data?.error ||
            error.response?.data?.message ||
            'Error al eliminar la firma'
          );
        }
      }
    });
  };

  const iniciarEdicion = () => {
    setEditando(true);
  };

  const cancelarEdicion = () => {
    setEditando(false);
    limpiarFirma();
  };

  if (cargando) {
    return (
      <Card title="Mi Firma" loading={true}>
        <p>Cargando firma...</p>
      </Card>
    );
  }

  return (
    <Card
      title="Mi Firma Digital"
      className="firma-conductor-card"
      extra={
        !editando && firmaActual && (
          <Space>
            <Button
              icon={<EditOutlined />}
              onClick={iniciarEdicion}
              type="primary"
            >
              Editar
            </Button>
            <Button
              icon={<DeleteOutlined />}
              onClick={eliminarFirma}
              danger
            >
              Eliminar
            </Button>
          </Space>
        )
      }
    >
      {editando ? (
        // Modo edición
        <div className="firma-editor">
          <Alert
            message="Dibuja tu firma"
            description="Usa tu dedo o mouse para dibujar tu firma. Esta firma se usará en los documentos de control de servicio."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <div className="signature-canvas-container">
            <SignatureCanvas
              ref={sigCanvas}
              canvasProps={{
                className: 'signature-canvas',
                width: window.innerWidth > 768 ? 600 : window.innerWidth - 80,
                height: 200
              }}
              backgroundColor="#ffffff"
              penColor="#000000"
            />
          </div>

          <Space style={{ marginTop: 16 }}>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={guardarFirma}
              loading={guardando}
            >
              Guardar Firma
            </Button>
            <Button
              onClick={limpiarFirma}
            >
              Limpiar
            </Button>
            <Button
              icon={<CloseOutlined />}
              onClick={cancelarEdicion}
            >
              Cancelar
            </Button>
          </Space>
        </div>
      ) : (
        // Modo visualización
        <div className="firma-visualizacion">
          {firmaActual ? (
            <>
              <Alert
                message="Firma configurada"
                description="Tu firma se usará automáticamente en los documentos de control de servicio."
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <div className="firma-preview">
                <img
                  src={firmaActual}
                  alt="Firma del conductor"
                  className="firma-imagen"
                />
              </div>
            </>
          ) : (
            <>
              <Alert
                message="Sin firma configurada"
                description="Configura tu firma para que aparezca automáticamente en los documentos de control de servicio."
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={iniciarEdicion}
                size="large"
              >
                Crear Mi Firma
              </Button>
            </>
          )}
        </div>
      )}
    </Card>
  );
};

export default FirmaConductor;
